import { Fragment, useState, useEffect, useCallback, useMemo } from "react";
import { Card, Form, Button, Table, Badge, Spinner, ProgressBar, Row, Col } from "react-bootstrap";
import { Link } from "react-router-dom";
import * as XLSX from "xlsx-js-style";
import swal from "sweetalert";
import {
 BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
 Legend, ResponsiveContainer, PieChart, Pie, Cell, Sector,
} from "recharts";
import api from "../../../api";
import { getProgressComparison } from "../../../services/progressService";

const PIE_COLORS = ["#28A745", "#FFC107", "#DC3545"];
const PLANNED_COLOR = "#0078D7";
const ACTUAL_COLOR = "#28A745";

const pct = (actual, planned) =>
 !planned ? 0 : Math.min(100, Math.round((actual / planned) * 100));

const statusInfo = (p) =>
 p >= 90
 ? { bg: "success", label: "On Track", icon: "fa-check-circle", color: "#28A745" }
 : p >= 60
 ? { bg: "warning", label: "Behind", icon: "fa-exclamation-triangle", color: "#FFC107" }
 : { bg: "danger", label: "Critical", icon: "fa-times-circle", color: "#DC3545" };

const exportDate = (date) =>
 date ? new Date(date).toLocaleDateString("en-IN") : "-";

const safeFilePart = (value) =>
 String(value || "WO").replace(/[\\/:*?"<>|]/g, "_");

const excelBorder = {
 top: { style: "thin", color: { rgb: "D9E2EC" } },
 bottom: { style: "thin", color: { rgb: "D9E2EC" } },
 left: { style: "thin", color: { rgb: "D9E2EC" } },
 right: { style: "thin", color: { rgb: "D9E2EC" } },
};

const applyCellStyle = (ws, cellRef, style) => {
 if (!ws[cellRef]) ws[cellRef] = { t: "s", v: "" };
 ws[cellRef].s = { ...(ws[cellRef].s || {}), ...style };
};

const applyRowStyle = (ws, rowNumber, fromCol, toCol, style) => {
 for (let col = fromCol; col <= toCol; col++) {
 applyCellStyle(ws, `${XLSX.utils.encode_col(col)}${rowNumber}`, style);
 }
};

const BarTooltip = ({ active, payload, label }) => {
 if (!active || !payload?.length) return null;
 return (
 <div style={{ background: "#fff", border: "1px solid #eee", borderRadius: 8, padding: "8px 14px", fontSize: 12, boxShadow: "0 2px 12px rgba(0,0,0,0.1)" }}>
 <p className="fw-bold mb-1">{label}</p>
 {payload.map((p) => (
 <p key={p.name} style={{ color: p.fill, margin: "2px 0" }}>
 {p.name}: <strong>{p.value}</strong> {p.payload.unit || ""}
 </p>
 ))}
 </div>
 );
};

const renderActivePieShape = (props) => {
 const RADIAN = Math.PI / 180;
 const {
 cx,
 cy,
 midAngle,
 innerRadius,
 outerRadius,
 startAngle,
 endAngle,
 fill,
 payload,
 value,
 percent,
 } = props;

 const sin = Math.sin(-RADIAN * midAngle);
 const cos = Math.cos(-RADIAN * midAngle);
 const highlightRadius = outerRadius + 10;
 const labelRadius = outerRadius + 20;

 const sx = cx + highlightRadius * cos;
 const sy = cy + highlightRadius * sin;
 const mx = cx + labelRadius * cos;
 const my = cy + labelRadius * sin;
 const ex = mx + (cos >= 0 ? 1 : -1) * 18;
 const ey = my;

 return (
 <g>
 <text x={cx} y={cy - 8} textAnchor="middle" fill="#1f2937" fontSize={12} fontWeight={700}>
 {payload?.name}
 </text>
 <text x={cx} y={cy + 10} textAnchor="middle" fill={fill} fontSize={12} fontWeight={600}>
 {typeof value === "number" ? value.toLocaleString("en-IN") : value}
 </text>
 <text x={cx} y={cy + 26} textAnchor="middle" fill="#6b7280" fontSize={10}>
 {Number.isFinite(percent) ? `${(percent * 100).toFixed(1)}%` : ""}
 </text>
 <path d={`M${sx},${sy}L${mx},${my}L${ex},${ey}`} stroke={fill} fill="none" />
 <circle cx={ex} cy={ey} r={2.5} fill={fill} stroke="none" />
 <Sector
 cx={cx}
 cy={cy}
 innerRadius={innerRadius}
 outerRadius={highlightRadius}
 startAngle={startAngle}
 endAngle={endAngle}
 fill={fill}
 />
 <Sector
 cx={cx}
 cy={cy}
 innerRadius={outerRadius + 2}
 outerRadius={outerRadius + 12}
 startAngle={startAngle}
 endAngle={endAngle}
 fill={fill}
 opacity={0.22}
 />
 </g>
 );
};

/* */
const PhysicalProgressReport = () => {
 const [workOrders, setWorkOrders] = useState([]);
 const [selectedWO, setSelectedWO] = useState("");
 const [fromDate, setFromDate] = useState("");
 const [toDate, setToDate] = useState(new Date().toISOString().split("T")[0]);
 const [rows, setRows] = useState([]);
 const [loading, setLoading] = useState(false);
 const [activeTab, setActiveTab] = useState("table");
 const [applied, setApplied] = useState(false);
 const [activePieIndex, setActivePieIndex] = useState(-1);

 /* work orders */
 const fetchWorkOrders = useCallback(async () => {
 try {
 const res = await api.get("/work-orders?limit=1000");
 if (res.data?.success) setWorkOrders(res.data.data);
 } catch { /* silent */ }
 }, []);

 useEffect(() => { fetchWorkOrders(); }, [fetchWorkOrders]);

 /* comparison */
 const fetchComparison = useCallback(async () => {
 if (!selectedWO) return;
 setLoading(true);
 setApplied(true);
 try {
 const params = { workOrder: selectedWO };
 if (fromDate) params.from = fromDate;
 if (toDate) params.to = toDate;
 const res = await getProgressComparison(params);
 setRows(res.data?.success ? res.data.data || [] : []);
 } catch (err) {
 if (err.response?.status !== 404 && err.response?.status !== 403)
 swal("Error", "Failed to load comparison data", "error");
 setRows([]);
 } finally { setLoading(false); }
 }, [selectedWO, fromDate, toDate]);

 /* summary */
 const summary = useMemo(() => {
 const overallPlanned = rows.reduce((s, r) => s + (r.plannedQuantity || 0), 0);
 const overallActual = rows.reduce((s, r) => s + (r.actualQuantity || 0), 0);
 return {
 total: rows.length,
 onTrack: rows.filter((r) => pct(r.actualQuantity, r.plannedQuantity) >= 90).length,
 behind: rows.filter((r) => { const p = pct(r.actualQuantity, r.plannedQuantity); return p >= 60 && p < 90; }).length,
 critical:rows.filter((r) => pct(r.actualQuantity, r.plannedQuantity) < 60).length,
 overallPlanned,
 overallActual,
 overallPct: pct(overallActual, overallPlanned),
 };
 }, [rows]);

 const pieData = useMemo(() => [
 { name: "On Track (≥90%)", value: summary.onTrack },
 { name: "Behind (60–89%)", value: summary.behind },
 { name: "Critical (<60%)", value: summary.critical },
 ], [summary]);

 const barData = useMemo(() =>
 [...rows]
 .sort((a, b) => (b.plannedQuantity || 0) - (a.plannedQuantity || 0))
 .slice(0, 12)
 .map((r) => ({
 name: (r.boqItemName || r.activityDescription || "—").substring(0, 16),
 Planned: r.plannedQuantity || 0,
 Actual: r.actualQuantity || 0,
 unit: r.unit || "",
 })),
 [rows]);

 /* export */
 const handleExport = () => {
 if (!rows.length) return;

 const wo = workOrders.find((w) => w._id === selectedWO);
 const projectName = wo?.project?.projectName || wo?.projectName || "-";
 const workOrderNo = wo?.woNo || wo?.woNumber || "-";
 const clientName = wo?.client?.clientName || wo?.client?.name || "-";
 const generatedOn = new Date().toLocaleString("en-IN");
 const varianceTotal = summary.overallPlanned - summary.overallActual;

 const detailRows = rows.map((r, i) => {
 const achievement = pct(r.actualQuantity, r.plannedQuantity);
 return [
 i + 1,
 r.boqItemId || "",
 r.boqItemName || "",
 r.activityDescription || "",
 r.unit || "",
 r.plannedQuantity || 0,
 r.actualQuantity || 0,
 (r.plannedQuantity || 0) - (r.actualQuantity || 0),
 `${achievement}%`,
 statusInfo(achievement).label,
 ];
 });

 const reportRows = [
 ["PHYSICAL PROGRESS REPORT", "", "", "", "", "", "", "", "", ""],
 [],
 ["Project", projectName, "", "Work Order", workOrderNo, "", "Client", clientName, "", ""],
 ["From Date", exportDate(fromDate), "", "To Date", exportDate(toDate), "", "Generated On", generatedOn, "", ""],
 [],
 ["Summary", "", "", "", "", "", "", "", "", ""],
 ["Total Activities", summary.total, "", "Total Planned", summary.overallPlanned, "", "Total Actual", summary.overallActual, "", ""],
 ["Overall Progress", `${summary.overallPct}%`, "", "Variance", varianceTotal, "", "On Track / Behind / Critical", `${summary.onTrack} / ${summary.behind} / ${summary.critical}`, "", ""],
 [],
 ["Detailed Activity List", "", "", "", "", "", "", "", "", ""],
 ["#", "BOQ ID", "BOQ Item", "Activity", "Unit", "Planned Qty", "Actual Completed", "Variance", "% Achievement", "Status"],
 ...detailRows,
 ["", "", "", "TOTAL", "", summary.overallPlanned, summary.overallActual, varianceTotal, `${summary.overallPct}%`, ""],
 ];

 const ws = XLSX.utils.aoa_to_sheet(reportRows);
 ws["!cols"] = [
 { wch: 12 },
 { wch: 24 },
 { wch: 26 },
 { wch: 22 },
 { wch: 18 },
 { wch: 18 },
 { wch: 24 },
 { wch: 22 },
 { wch: 16 },
 { wch: 14 },
 ];
 ws["!merges"] = [
 { s: { r: 0, c: 0 }, e: { r: 0, c: 9 } },
 { s: { r: 2, c: 1 }, e: { r: 2, c: 2 } },
 { s: { r: 2, c: 4 }, e: { r: 2, c: 5 } },
 { s: { r: 2, c: 7 }, e: { r: 2, c: 9 } },
 { s: { r: 3, c: 1 }, e: { r: 3, c: 2 } },
 { s: { r: 3, c: 4 }, e: { r: 3, c: 5 } },
 { s: { r: 3, c: 7 }, e: { r: 3, c: 9 } },
 { s: { r: 5, c: 0 }, e: { r: 5, c: 9 } },
 { s: { r: 9, c: 0 }, e: { r: 9, c: 9 } },
 ];
 ws["!autofilter"] = { ref: `A11:J${reportRows.length}` };
 ws["!freeze"] = { xSplit: 0, ySplit: 11 };
 ws["!rows"] = reportRows.map((_, index) => ({
 hpt: index === 0 ? 30 : [2, 3].includes(index) ? 26 : [5, 9].includes(index) ? 24 : index === 10 ? 22 : 19,
 }));

 const titleStyle = {
 font: { bold: true, sz: 18, color: { rgb: "FFFFFF" } },
 fill: { fgColor: { rgb: "1F4E78" } },
 alignment: { horizontal: "center", vertical: "center" },
 border: excelBorder,
 };
 const sectionStyle = {
 font: { bold: true, sz: 12, color: { rgb: "FFFFFF" } },
 fill: { fgColor: { rgb: "5B9BD5" } },
 alignment: { horizontal: "center", vertical: "center" },
 border: excelBorder,
 };
 const labelStyle = {
 font: { bold: true, color: { rgb: "1F4E78" } },
 fill: { fgColor: { rgb: "DDEBF7" } },
 alignment: { horizontal: "center", vertical: "center" },
 border: excelBorder,
 };
 const valueStyle = {
 fill: { fgColor: { rgb: "F8FBFD" } },
 alignment: { horizontal: "center", vertical: "center", wrapText: true },
 border: excelBorder,
 };
 const tableHeaderStyle = {
 font: { bold: true, color: { rgb: "FFFFFF" } },
 fill: { fgColor: { rgb: "2F5597" } },
 alignment: { horizontal: "center", vertical: "center", wrapText: true },
 border: excelBorder,
 };
 const bodyStyle = {
 alignment: { vertical: "center", wrapText: true },
 border: excelBorder,
 };
 const numberStyle = {
 alignment: { horizontal: "right", vertical: "center" },
 border: excelBorder,
 };
 const totalStyle = {
 font: { bold: true, color: { rgb: "FFFFFF" } },
 fill: { fgColor: { rgb: "70AD47" } },
 alignment: { horizontal: "right", vertical: "center" },
 border: excelBorder,
 };

 applyRowStyle(ws, 1, 0, 9, titleStyle);
 applyRowStyle(ws, 6, 0, 9, sectionStyle);
 applyRowStyle(ws, 10, 0, 9, sectionStyle);
 applyRowStyle(ws, 11, 0, 9, tableHeaderStyle);

 [3, 4, 7, 8].forEach((rowNumber) => {
 [0, 3, 6].forEach((col) => applyCellStyle(ws, `${XLSX.utils.encode_col(col)}${rowNumber}`, labelStyle));
 [1, 2, 4, 5, 7, 8, 9].forEach((col) => applyCellStyle(ws, `${XLSX.utils.encode_col(col)}${rowNumber}`, valueStyle));
 });

 for (let rowNumber = 12; rowNumber <= reportRows.length - 1; rowNumber++) {
 applyRowStyle(ws, rowNumber, 0, 9, bodyStyle);
 [0, 5, 6, 7].forEach((col) => {
 applyCellStyle(ws, `${XLSX.utils.encode_col(col)}${rowNumber}`, numberStyle);
 });
 applyCellStyle(ws, `I${rowNumber}`, {
 alignment: { horizontal: "center", vertical: "center" },
 border: excelBorder,
 });

 const status = ws[`J${rowNumber}`]?.v;
 const statusFill =
 status === "On Track" ? "E2F0D9" : status === "Behind" ? "FFF2CC" : "FCE4D6";
 const statusColor =
 status === "On Track" ? "548235" : status === "Behind" ? "9C6500" : "C00000";
 applyCellStyle(ws, `J${rowNumber}`, {
 font: { bold: true, color: { rgb: statusColor } },
 fill: { fgColor: { rgb: statusFill } },
 alignment: { horizontal: "center", vertical: "center" },
 border: excelBorder,
 });
 }

 applyRowStyle(ws, reportRows.length, 0, 9, totalStyle);
 ["A", "B", "C", "D", "E", "I", "J"].forEach((col) => {
 applyCellStyle(ws, `${col}${reportRows.length}`, {
 alignment: { horizontal: "center", vertical: "center" },
 });
 });

 const wb = XLSX.utils.book_new();
 XLSX.utils.book_append_sheet(wb, ws, "Progress Report");
 XLSX.writeFile(wb, `Progress_Report_${safeFilePart(workOrderNo)}.xlsx`);
 };

 const selectedWOObj = workOrders.find((w) => w._id === selectedWO);

 /* RENDER */
 return (
 <Fragment>

 {/* Page header */}
 <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-2">
 <div>
 <h4 className="fw-bold mb-1">
 <i className="fas fa-chart-bar text-primary me-2" />
 Physical Progress Report
 </h4>
 <p className="text-muted mb-0 small">
 Compare planned quantity against actual completed quantity entered in the progress template.
 </p>
 </div>
 <Link to="/progress/plan" className="btn btn-outline-secondary btn-sm">
 <i className="fas fa-cog me-1" /> Manage Plan
 </Link>
 </div>

 {/* Filter card */}
 <Card className="border-0 shadow-sm mb-4">
 <Card.Body className="p-4">
 <p className="fw-semibold small text-muted text-uppercase mb-3" style={{ letterSpacing: "0.05em" }}>
 <i className="fas fa-filter me-2 text-primary" />
 Filter
 </p>
 <Row className="g-3 align-items-end">
 <Col md={5}>
 <Form.Label className="fw-semibold small mb-1">
 Work Order <span className="text-danger">*</span>
 </Form.Label>
 <Form.Select value={selectedWO} onChange={(e) => { setSelectedWO(e.target.value); setApplied(false); setRows([]); }} style={{ height: 42 }}>
 <option value="">-- Select Work Order --</option>
 {workOrders.map((wo) => (
 <option key={wo._id} value={wo._id}>{wo.woNo}{wo.label ? ` – ${wo.label}` : ""} — {wo.project?.projectName}</option>
 ))}
 </Form.Select>
 </Col>
 <Col md={2}>
 <Form.Label className="fw-semibold small mb-1">From Date</Form.Label>
 <Form.Control type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} style={{ height: 42 }} />
 </Col>
 <Col md={2}>
 <Form.Label className="fw-semibold small mb-1">To Date</Form.Label>
 <Form.Control type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} style={{ height: 42 }} />
 </Col>
 <Col md={3}>
 <Button
 variant="primary"
 className="w-100"
 style={{ height: 42 }}
 onClick={fetchComparison}
 disabled={!selectedWO || loading}
 >
 {loading
 ? <><Spinner size="sm" className="me-2" />Loading…</>
 : <><i className="fas fa-search me-2" />Generate Report</>}
 </Button>
 </Col>
 </Row>
 </Card.Body>
 </Card>

 {/* Not yet applied */}
 {!applied && (
 <Card className="border-0 shadow-sm">
 <Card.Body className="text-center py-5 text-muted">
 <i className="fas fa-chart-bar fs-1 mb-3 d-block opacity-25" />
 <h6 className="fw-semibold">Select a Work Order and click "Generate Report"</h6>
 <p className="small mb-3">The report compares planned and actual quantities from the uploaded template.</p>
 <Link to="/progress/plan" className="btn btn-outline-primary btn-sm">
 <i className="fas fa-upload me-1" /> Upload a Plan first
 </Link>
 </Card.Body>
 </Card>
 )}

 {/* Applied but loading */}
 {applied && loading && (
 <Card className="border-0 shadow-sm">
 <Card.Body className="text-center py-5">
 <Spinner animation="border" variant="primary" />
 <p className="text-muted mt-3 mb-0 small">Fetching comparison data…</p>
 </Card.Body>
 </Card>
 )}

 {/* Results */}
 {applied && !loading && (
 <>
 {/* Summary stat cards */}
 <Row className="g-3 mb-4">
 {[
 { label: "Total Activities", value: summary.total, icon: "fa-list", color: "#0078D7" },
 { label: "On Track (≥90%)", value: summary.onTrack, icon: "fa-check-circle", color: "#28A745" },
 { label: "Behind (60–89%)", value: summary.behind, icon: "fa-exclamation-triangle",color: "#FFC107" },
 { label: "Critical (<60%)", value: summary.critical, icon: "fa-times-circle", color: "#DC3545" },
 { label: "Overall Progress", value: `${summary.overallPct}%`, icon: "fa-percentage", color: "#6F42C1" },
 ].map((c, i) => (
 <Col key={i} xl={2} sm={4} xs={6}>
 <div className="card border-0 shadow-sm h-100" style={{ borderLeft: `4px solid ${c.color}` }}>
 <div className="card-body d-flex align-items-center p-3 gap-3">
 <div className="rounded-circle d-flex align-items-center justify-content-center flex-shrink-0"
 style={{ width: 40, height: 40, background: `${c.color}18` }}>
 <i className={`fas ${c.icon}`} style={{ color: c.color, fontSize: 15 }} />
 </div>
 <div>
 <p className="mb-0 text-muted" style={{ fontSize: 10 }}>{c.label}</p>
 <h5 className="mb-0 fw-bold">{c.value}</h5>
 </div>
 </div>
 </div>
 </Col>
 ))}

 {/* Overall progress bar card */}
 <Col xl={2} sm={12}>
 <div className="card border-0 shadow-sm h-100">
 <div className="card-body p-3">
 <p className="mb-1 text-muted" style={{ fontSize: 10 }}>Overall Achievement</p>
 <ProgressBar
 now={summary.overallPct}
 variant={summary.overallPct >= 90 ? "success" : summary.overallPct >= 60 ? "warning" : "danger"}
 style={{ height: 10, borderRadius: 6 }}
 />
 <p className="mb-0 fw-bold mt-1" style={{ fontSize: 13 }}>
 {summary.overallActual} / {summary.overallPlanned}
 </p>
 </div>
 </div>
 </Col>
 </Row>

 {/* No data state */}
 {rows.length === 0 ? (
 <Card className="border-0 shadow-sm">
 <Card.Body className="text-center py-5 text-muted">
 <i className="fas fa-inbox fs-1 mb-3 d-block opacity-25" />
 <h6 className="fw-semibold">No comparison data found</h6>
 <p className="small mb-3">
 Either no plan is uploaded for this Work Order, or actual quantities are not entered in the selected date range.
 </p>
 <Link to="/progress/plan" className="btn btn-primary btn-sm">
 <i className="fas fa-upload me-1" /> Upload Plan
 </Link>
 </Card.Body>
 </Card>
 ) : (
 <Card className="border-0 shadow-sm">
 {/* Tabs + Export toolbar */}
 <Card.Header className="bg-white border-bottom d-flex justify-content-between align-items-center py-3 flex-wrap gap-2">
 <div className="d-flex gap-0 rounded overflow-hidden border">
 {[
 { key: "table", icon: "fa-table", label: "Table" },
 { key: "bar", icon: "fa-chart-bar", label: "Bar Chart" },
 { key: "pie", icon: "fa-chart-pie", label: "Pie Chart" },
 ].map((t) => (
 <button
 key={t.key}
 type="button"
 onClick={() => setActiveTab(t.key)}
 className={`btn btn-sm px-3 rounded-0 ${activeTab === t.key ? "btn-primary" : "btn-light"}`}
 style={{ fontSize: 13 }}
 >
 <i className={`fas ${t.icon} me-1`} />{t.label}
 </button>
 ))}
 </div>
 <div className="d-flex gap-2 align-items-center">
 {selectedWOObj && (
 <span className="text-muted small">
 {selectedWOObj.woNo} · {selectedWOObj.project?.projectName}
 </span>
 )}
 <Button variant="outline-success" size="sm" onClick={handleExport}>
 <i className="fas fa-file-excel me-1" /> Export Excel
 </Button>
 </div>
 </Card.Header>

 <Card.Body className={activeTab === "table" ? "p-0" : "p-4"}>

 {/* TABLE */}
 {activeTab === "table" && (
 <div className="table-responsive">
 <Table hover className="mb-0 align-middle" style={{ fontSize: 13 }}>
 <thead className="table-light">
 <tr>
 <th className="ps-3">#</th>
 <th>BOQ ID</th>
 <th>BOQ Item / Activity</th>
 <th>Unit</th>
 <th className="text-end" style={{ color: PLANNED_COLOR }}>Planned</th>
 <th className="text-end" style={{ color: ACTUAL_COLOR }}>Actual</th>
 <th className="text-end">Variance</th>
 <th style={{ minWidth: 160 }}>Achievement</th>
 <th>Status</th>
 </tr>
 </thead>
 <tbody>
 {rows.map((r, i) => {
 const achievement = pct(r.actualQuantity, r.plannedQuantity);
 const variance = (r.plannedQuantity || 0) - (r.actualQuantity || 0);
 const st = statusInfo(achievement);
 return (
 <tr key={r._id || i}>
 <td className="ps-3 text-muted">{i + 1}</td>
 <td><code className="text-primary">{r.boqItemId || "—"}</code></td>
 <td>
 <div className="fw-semibold">{r.boqItemName || "—"}</div>
 <small className="text-muted">{r.activityDescription}</small>
 </td>
 <td>{r.unit}</td>
 <td className="text-end fw-bold" style={{ color: PLANNED_COLOR }}>{r.plannedQuantity ?? "—"}</td>
 <td className="text-end fw-bold" style={{ color: ACTUAL_COLOR }}>{r.actualQuantity ?? 0}</td>
 <td className={`text-end fw-bold ${variance > 0 ? "text-warning" : variance < 0 ? "text-success" : "text-muted"}`}>
 {variance}
 </td>
 <td>
 <div className="d-flex align-items-center gap-2">
 <ProgressBar now={achievement} variant={st.bg} style={{ flex: 1, height: 7, borderRadius: 4 }} />
 <span className="fw-bold" style={{ width: 36, fontSize: 12 }}>{achievement}%</span>
 </div>
 </td>
 <td><Badge bg={st.bg} style={{ fontSize: 10 }}>{st.label}</Badge></td>
 </tr>
 );
 })}
 </tbody>
 <tfoot className="table-light fw-bold">
 <tr>
 <td colSpan={4} className="ps-3">Total</td>
 <td className="text-end" style={{ color: PLANNED_COLOR }}>{summary.overallPlanned}</td>
 <td className="text-end" style={{ color: ACTUAL_COLOR }}>{summary.overallActual}</td>
 <td className={`text-end ${summary.overallPlanned - summary.overallActual > 0 ? "text-warning" : summary.overallPlanned - summary.overallActual < 0 ? "text-success" : "text-muted"}`}>
 {summary.overallPlanned - summary.overallActual}
 </td>
 <td colSpan={2}>{summary.overallPct}% Overall</td>
 </tr>
 </tfoot>
 </Table>
 </div>
 )}

 {/* BAR CHART */}
 {activeTab === "bar" && (
 <>
 <p className="text-muted small mb-3">Top {barData.length} activities by planned quantity</p>
 <ResponsiveContainer width="100%" height={400}>
 <BarChart data={barData} margin={{ top: 5, right: 20, left: 0, bottom: 80 }} barCategoryGap="30%">
 <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
 <XAxis dataKey="name" angle={-40} textAnchor="end" interval={0} tick={{ fontSize: 11 }} />
 <YAxis tick={{ fontSize: 11 }} />
 <Tooltip content={<BarTooltip />} />
 <Legend iconType="circle" wrapperStyle={{ fontSize: 12, paddingTop: 8 }} />
 <Bar dataKey="Planned" fill={PLANNED_COLOR} radius={[4, 4, 0, 0]} maxBarSize={36} />
 <Bar dataKey="Actual" fill={ACTUAL_COLOR} radius={[4, 4, 0, 0]} maxBarSize={36} />
 </BarChart>
 </ResponsiveContainer>
 </>
 )}

 {/* PIE CHART */}
 {activeTab === "pie" && (
 <Row className="align-items-center">
 <Col md={5}>
 <ResponsiveContainer width="100%" height={300}>
 <PieChart>
 <Pie data={pieData} dataKey="value" nameKey="name" cx="50%" cy="50%"
 outerRadius={110} innerRadius={55} paddingAngle={4}
 activeIndex={activePieIndex}
 activeShape={renderActivePieShape}
 isAnimationActive
 animationBegin={150}
 animationDuration={900}
 animationEasing="ease-out"
 onMouseEnter={(_, index) => setActivePieIndex(index)}
 onMouseLeave={() => setActivePieIndex(-1)}
 label={({ value }) => value > 0 ? value : ""} labelLine={false}
 stroke="#ffffff"
 strokeWidth={2}>
 {pieData.map((_, idx) => (
 <Cell
 key={idx}
 fill={PIE_COLORS[idx % PIE_COLORS.length]}
 opacity={activePieIndex === -1 || activePieIndex === idx ? 1 : 0.35}
 />
 ))}
 </Pie>
 <Tooltip formatter={(v) => [v, "Activities"]} contentStyle={{ borderRadius: 8 }} />
 <Legend iconType="circle" wrapperStyle={{ fontSize: 12 }} />
 </PieChart>
 </ResponsiveContainer>
 </Col>
 <Col md={7}>
 <h6 className="fw-bold mb-3">Overall Achievement Summary</h6>
 <div className="mb-4">
 <div className="d-flex justify-content-between mb-1 small">
 <span className="fw-semibold">Overall Physical Progress</span>
 <span className="fw-bold">{summary.overallPct}%</span>
 </div>
 <ProgressBar
 now={summary.overallPct}
 variant={summary.overallPct >= 90 ? "success" : summary.overallPct >= 60 ? "warning" : "danger"}
 style={{ height: 14, borderRadius: 8 }}
 />
 </div>
 <Table bordered size="sm" style={{ fontSize: 13 }}>
 <tbody>
 <tr><td>Total Planned</td> <td className="fw-bold" style={{ color: PLANNED_COLOR }}>{summary.overallPlanned}</td></tr>
 <tr><td>Total Actual</td> <td className="fw-bold" style={{ color: ACTUAL_COLOR }}>{summary.overallActual}</td></tr>
 <tr>
 <td>Variance</td>
 <td className={`fw-bold ${summary.overallPlanned - summary.overallActual > 0 ? "text-warning" : summary.overallPlanned - summary.overallActual < 0 ? "text-success" : "text-muted"}`}>
 {summary.overallPlanned - summary.overallActual}
 </td>
 </tr>
 <tr><td>On Track Activities</td> <td className="fw-bold text-success">{summary.onTrack}</td></tr>
 <tr><td>Behind Activities</td> <td className="fw-bold text-warning">{summary.behind}</td></tr>
 <tr><td>Critical Activities</td> <td className="fw-bold text-danger">{summary.critical}</td></tr>
 </tbody>
 </Table>
 </Col>
 </Row>
 )}
 </Card.Body>
 </Card>
 )}
 </>
 )}
 </Fragment>
 );
};

export default PhysicalProgressReport;
